import { ChangeEvent, DetailedHTMLProps, forwardRef, HTMLAttributes, InputHTMLAttributes, memo, useId, useMemo, useRef } from "react";
import classNames from "classnames";
import { RefType } from "../../../../types";
import { useMergedRef } from "../../../../utils";

import { ReactComponent as CheckedIcon } from "./icons/check.svg";

import styles from "./style.module.scss";

export type RadioButtonBaseSize = "sm" | "md";

type OptionValue = string | number | readonly string[];

export type RadioButtonBaseProps = Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, "value" | "onChange" | "ref"> & {
  optionValue: OptionValue,
  value?: OptionValue,
  onChange?: (value: OptionValue, e: ChangeEvent<HTMLInputElement>) => void,
  containerProps?: DetailedHTMLProps<HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>
  inputRef?: RefType<HTMLInputElement>
  radioButtonBaseSize?: RadioButtonBaseSize,
  inputState?: unknown
  hintText?: unknown
}

export const RadioButtonBase = memo(forwardRef<HTMLSpanElement, RadioButtonBaseProps>((
  {
    id,
    value,
    checked,
    disabled,
    onChange,
    className,
    containerProps,
    inputRef,
    optionValue,
    radioButtonBaseSize = "md",
    inputState,
    hintText,
    ...props
  },
  ref,
) => {
  const resolvedRef = useRef<HTMLInputElement>();
  const reactId = useId();
  const realId = id || reactId;
  const isChecked = checked || value === optionValue;
  const mergedInputRef = useMergedRef(resolvedRef, inputRef);

  const onValueChanged = useMemo(() => (e: ChangeEvent<HTMLInputElement>) => {
    if (!disabled) {
      onChange?.(optionValue!, e);
    }
  }, [ disabled, onChange, optionValue ]);

  return (
    <span
      {...containerProps}
      className={classNames(
        styles.root,
        className,
        styles[radioButtonBaseSize as keyof typeof styles],
      )}
      data-input-type="radio"
      data-input-state={isChecked}
      data-input-disabled={disabled}
      data-checkbox-base-size={radioButtonBaseSize}
      ref={ref}
    >
      <input
        {...props}
        id={realId}
        ref={mergedInputRef}
        value={optionValue}
        checked={isChecked}
        disabled={disabled}
        onChange={onValueChanged}
        data-checked={isChecked}
        type="radio"
      />
      <span role="button" className={styles.icon}>
        {isChecked ? <CheckedIcon /> : null}
      </span>
    </span>
  );
}));